import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

import { useTranslation } from '@lyearn/i18n';
import { Button, Image, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import RichTextViewer from '@/components/RichTextViewer';
import { BadgeType, EntityMediaType } from '@/types/schema';

import { PointsGoldLarge } from './PointsGoldLarge';

const BadgePreviewModal = ({
  badge,
  open,
  onClose,
  isPreview = true,
  onNext,
  onPrev,
  showPrev = true,
  showNext = true,
}: {
  badge: Pick<BadgeType, 'id' | 'name' | 'achievementRemark' | 'pointsConfig'> & {
    media?: Pick<EntityMediaType, 'url' | 'kind'> | null;
  };
  open: boolean;
  onClose: () => void;
  isPreview?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  showPrev?: boolean;
  showNext?: boolean;
}) => {
  const { t } = useTranslation(['Gamification']);
  const points = badge?.pointsConfig?.points || 0;
  const { width, height } = useWindowSize();
  return (
    <Modal classes={{ paper: 'h-full' }} open={open} showCloseBtn={false} onClose={onClose}>
      <Confetti gravity={0.2} height={height} width={width} />
      <Stack className="h-full" direction="col" justifyContent="between">
        <Stack alignItems="center" className="p-32" direction="col" justifyContent="center">
          <Image
            containerStyle={{
              borderRadius: '9999px',
            }}
            height={250}
            src={badge.media?.url || ''}
            style={{
              borderRadius: '9999px',
            }}
            width={250}
          />
          <Stack
            alignItems="center"
            className="mt-32"
            direction="col"
            gapY={4}
            justifyContent="center">
            <Typography variant="heading-xl">{badge.name}</Typography>
            <Typography
              className="text-text-secondary text-center"
              numberOfLines={5}
              variant="body-short-01">
              <RichTextViewer html={badge.achievementRemark ?? ''} />
            </Typography>
            {points > 0 ? (
              <Stack
                className="bg-bg-secondary rounded-8 p-16 min-w-[174px] h-[84px] mt-32"
                gapX={8}>
                <PointsGoldLarge />
                <Stack direction="col" gapY={4}>
                  <Typography variant="heading-s">
                    {t('Gamification:PointsCount', { count: points })}
                  </Typography>
                  <Typography className="text-text-secondary" variant="body-short-02">
                    {t('Gamification:Unlocked')}
                  </Typography>
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
        <Stack direction="col">
          <Stack className="h-[1px] my-[24px] w-full bg-border" justifyContent="end" />
          {isPreview ? (
            <Stack className="mx-16 mb-24">
              <Button fullWidth size="large" onClick={onClose}>
                OK
              </Button>
            </Stack>
          ) : (
            <Stack className="mx-16 mb-24" direction="row" gapX={16}>
              {showPrev ? (
                <Button fullWidth disabled={!showPrev} size="large" onClick={onPrev}>
                  {t('Common:Actions.Previous')}
                </Button>
              ) : null}
              {showNext ? (
                <Button fullWidth size="large" onClick={onNext}>
                  {t('Common:Actions.Next')}
                </Button>
              ) : (
                <Button fullWidth size="large" onClick={onClose}>
                  {t('Gamification:ContinueLearning')}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};

export default BadgePreviewModal;
